<template>
  <div class="roi-overview-page">
    <div class="filter-bar">
      <div class="filter-row">
        <span class="filter-label">统计类别</span>
        <el-button-group class="filter-stat-btns">
          <el-button :type="searchForm.statCategory === 'range' ? 'primary' : ''" @click="searchForm.statCategory = 'range'">按时间段汇总</el-button>
          <el-button :type="searchForm.statCategory === 'year' ? 'primary' : ''" @click="searchForm.statCategory = 'year'">按年</el-button>
          <el-button :type="searchForm.statCategory === 'month' ? 'primary' : ''" @click="searchForm.statCategory = 'month'">按月</el-button>
          <el-button :type="searchForm.statCategory === 'week' ? 'primary' : ''" @click="searchForm.statCategory = 'week'">按周</el-button>
          <el-button :type="searchForm.statCategory === 'day' ? 'primary' : ''" @click="searchForm.statCategory = 'day'">按日</el-button>
        </el-button-group>
        <div class="filter-date-slot">
          <el-date-picker
            v-if="searchForm.statCategory === 'range'"
            key="picker-range"
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            :picker-options="rangePickerOptions"
            clearable
            class="filter-date filter-date-range"
          />
          <el-date-picker
            v-else
            :key="'picker-' + searchForm.statCategory"
            v-model="searchForm.startDate"
            :type="datePickerType"
            :placeholder="datePickerPlaceholder"
            :format="datePickerFormat"
            :value-format="datePickerValueFormat"
            :picker-options="pickerDateOptions"
            clearable
            class="filter-date"
          />
        </div>
        <el-button type="primary" icon="el-icon-search" @click="handleQuery" :loading="queryLoading">查询</el-button>
        <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        <span v-if="searchForm.statCategory === 'range'" class="range-hint">支持的最大时间区间: 730天</span>
      </div>
    </div>
    <el-card shadow="never" class="table-card">
      <el-table
        :data="tableData"
        border
        stripe
        size="small"
        v-loading="loading"
        class="overview-table"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="platformName" label="平台名称" min-width="100" align="center" show-overflow-tooltip />
        <el-table-column prop="accountName" label="账户名" min-width="120" align="center" show-overflow-tooltip />
        <el-table-column prop="balance" label="余额" min-width="120" align="right" show-overflow-tooltip />
        <el-table-column prop="consumption" label="消费" min-width="120" align="right" show-overflow-tooltip />
        <el-table-column prop="chainRatio" label="环比" min-width="100" align="right" show-overflow-tooltip />
        <el-table-column prop="yearOnYear" label="同比" min-width="100" align="right" show-overflow-tooltip />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import api from '@/services/api';

function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getWeekMonday(value) {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date);
  monday.setDate(diff);
  return monday;
}

function getDefaultStartDate(statCategory) {
  const now = new Date();
  switch (statCategory) {
    case 'year':
      return String(now.getFullYear());
    case 'month':
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    case 'week':
      return formatDate(getWeekMonday(now));
    case 'day':
      return formatDate(now);
    default:
      return formatDate(now);
  }
}

export default {
  name: 'ROIOverview',
  data() {
    return {
      loading: false,
      queryLoading: false,
      rangePickerFirst: null,
      searchForm: {
        statCategory: 'day',
        startDate: getDefaultStartDate('day'),
        dateRange: null
      },
      tableData: []
    };
  },
  computed: {
    datePickerType() {
      const map = { year: 'year', month: 'month', week: 'week', day: 'date' };
      return map[this.searchForm.statCategory] || 'date';
    },
    datePickerPlaceholder() {
      const map = { year: '选择年份', month: '选择月份', week: '选择周', day: '选择日期' };
      return map[this.searchForm.statCategory] || '请选择日期';
    },
    datePickerFormat() {
      if (this.searchForm.statCategory === 'week') return 'yyyy 第 WW 周';
      return null;
    },
    datePickerValueFormat() {
      const map = { year: 'yyyy', month: 'yyyy-MM', week: 'yyyy-MM-dd', day: 'yyyy-MM-dd' };
      return map[this.searchForm.statCategory] || 'yyyy-MM-dd';
    },
    pickerDateOptions() {
      if (this.searchForm.statCategory === 'year') {
        return {
          disabledDate(time) {
            return time.getFullYear() > new Date().getFullYear();
          },
          shortcuts: [
            { text: '今年', onClick(picker) {
              picker.$emit('pick', `${new Date().getFullYear()}`);
            }},
            { text: '去年', onClick(picker) {
              picker.$emit('pick', `${new Date().getFullYear() - 1}`);
            }}
          ]
        };
      }
      if (this.searchForm.statCategory === 'month') {
        return {
          disabledDate(time) {
            const now = new Date();
            return time.getFullYear() > now.getFullYear()
              || (time.getFullYear() === now.getFullYear() && time.getMonth() > now.getMonth());
          },
          shortcuts: [
            { text: '本月', onClick(picker) {
              const now = new Date();
              picker.$emit('pick', `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
            }},
            { text: '上月', onClick(picker) {
              const prev = new Date();
              prev.setMonth(prev.getMonth() - 1);
              picker.$emit('pick', `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
            }}
          ]
        };
      }
      if (this.searchForm.statCategory === 'week') {
        return {
          firstDayOfWeek: 1,
          disabledDate(time) {
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            return time.getTime() > today.getTime();
          },
          shortcuts: [
            { text: '本周', onClick(picker) {
              picker.$emit('pick', formatDate(getWeekMonday(new Date())));
            }},
            { text: '上周', onClick(picker) {
              const monday = getWeekMonday(new Date());
              monday.setDate(monday.getDate() - 7);
              picker.$emit('pick', formatDate(monday));
            }}
          ]
        };
      }
      if (this.searchForm.statCategory === 'day') {
        return {
          disabledDate(time) {
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            return time.getTime() > today.getTime();
          },
          shortcuts: [
            { text: '今天', onClick(picker) {
              picker.$emit('pick', formatDate(new Date()));
            }},
            { text: '昨天', onClick(picker) {
              const d = new Date();
              d.setDate(d.getDate() - 1);
              picker.$emit('pick', formatDate(d));
            }}
          ]
        };
      }
      return {};
    },
    rangePickerOptions() {
      const MAX_DAYS = 730;
      return {
        onPick: ({ minDate }) => {
          this.rangePickerFirst = minDate;
        },
        disabledDate: (time) => {
          const range = this.searchForm.dateRange;
          const anchor = this.rangePickerFirst || (range && range[0] ? new Date(range[0]) : null);
          if (!anchor) return false;
          const t = time.getTime();
          const a = anchor.getTime();
          const diffDays = Math.round((t - a) / (86400 * 1000));
          return Math.abs(diffDays) > MAX_DAYS;
        }
      };
    }
  },
  watch: {
    'searchForm.statCategory'(val) {
      if (val === 'range') {
        this.searchForm.startDate = '';
        this.searchForm.dateRange = this.getDefaultDateRange();
      } else {
        this.searchForm.dateRange = null;
        this.rangePickerFirst = null;
        this.searchForm.startDate = getDefaultStartDate(val);
      }
    },
    'searchForm.dateRange'(val) {
      if (!val) this.rangePickerFirst = null;
    }
  },
  mounted() {
    if (this.searchForm.statCategory === 'range') {
      this.searchForm.dateRange = this.getDefaultDateRange();
    } else {
      this.searchForm.startDate = getDefaultStartDate(this.searchForm.statCategory);
    }
    this.loadData();
  },
  methods: {
    getDefaultDateRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return [
        `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
        `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
      ];
    },
    async loadData() {
      this.loading = true;
      try {
        const params = { statCategory: this.searchForm.statCategory };
        if (this.searchForm.statCategory === 'range') {
          const range = this.searchForm.dateRange;
          if (!range || !range[0] || !range[1]) {
            this.tableData = [];
            this.loading = false;
            return;
          }
          const start = new Date(range[0]);
          const end = new Date(range[1]);
          const diffDays = Math.round((end - start) / (86400 * 1000));
          if (diffDays < 0 || diffDays > 730) {
            this.$message.error('日期范围不能超过730天');
            this.tableData = [];
            this.loading = false;
            return;
          }
          params.startDate = range[0];
          params.endDate = range[1];
        } else {
          if (!this.searchForm.startDate) {
            this.tableData = [];
            this.loading = false;
            return;
          }
          params.startDate = this.searchForm.startDate;
        }
        const res = await api.post('/promotion-account/overview-report', params);
        if (!res.data.success) {
          this.tableData = [];
          return;
        }
        const list = res.data.data || [];
        // 按平台名称聚合，便于合并「平台名称」列；同平台内按账户名排序
        list.sort((a, b) => {
          const pa = String(a.platformName || '').trim();
          const pb = String(b.platformName || '').trim();
          if (pa !== pb) return pa.localeCompare(pb, 'zh-CN');
          return String(a.accountName || '').localeCompare(String(b.accountName || ''), 'zh-CN');
        });
        this.tableData = list;
      } catch (e) {
        this.$message.error(e.response?.data?.message || '加载数据失败');
        this.tableData = [];
      } finally {
        this.loading = false;
      }
    },
    async handleQuery() {
      this.queryLoading = true;
      try {
        await this.loadData();
      } finally {
        this.queryLoading = false;
      }
    },
    handleReset() {
      this.searchForm.statCategory = 'day';
      this.searchForm.startDate = getDefaultStartDate('day');
      this.searchForm.dateRange = null;
      this.rangePickerFirst = null;
      this.loadData();
    }
  }
};
</script>

<style scoped>
.roi-overview-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.filter-bar {
  flex-shrink: 0;
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 16px;
}
.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.filter-label {
  width: 80px;
  flex-shrink: 0;
  color: #606266;
  font-size: 14px;
  text-align: right;
  align-self: center;
}
/* 与日期选择器同一行时避免基线错位导致整体上移 */
.filter-stat-btns {
  align-self: center;
  flex-shrink: 0;
  display: inline-flex;
  vertical-align: middle;
}
.filter-date-slot {
  display: flex;
  align-items: center;
  align-self: center;
  flex-shrink: 0;
  min-height: 40px;
}
.filter-date-slot >>> .el-date-editor {
  vertical-align: middle;
}
/* 与 el-button 默认(medium)高度对齐，切换 按周/按日/按月 时布局稳定 */
.filter-date-slot >>> .el-date-editor .el-input__inner {
  height: 40px;
  line-height: 40px;
}
.filter-date-slot >>> .el-date-editor.el-range-editor.el-input__inner {
  height: 40px;
  line-height: 38px;
}
.filter-date {
  width: 180px;
}
.filter-date-range {
  width: 350px;
}
.filter-date-range >>> .el-range-separator {
  width: 7%;
}
.range-hint {
  color: #909399;
  font-size: 13px;
}
.table-card {
  flex: 1;
}
.overview-table {
  margin-top: 0;
}
</style>
